import * as React from 'react';
import { compose, setDisplayName, withHandlers, withState, withProps, lifecycle } from 'recompose';
import { TranslateProps } from 'util/i18n';
import { ReportSettings, ReportSettingsChangeHandler } from 'logic/reports/ReportSettings';
import { MonthPicker } from './MonthPicker';
import { makeSentenceFragments, FragmentRenderer, renderFragment } from 'util/makeSentenceFragments';
import { Input } from 'ui/shared/Input';

export type OwnProps = TranslateProps & {
    enabled: boolean;
    useDayNum: boolean;
    onChangeUseDayNum: (b: boolean) => void;
    reportSettings: Readonly<ReportSettings>;
    onChange: ReportSettingsChangeHandler;
};

const RawRecurrenceYearlyDayNum = ({ useDayNum, handleChangeUseDayNum, enabled, sentenceFragments, renderFragment }: OwnProps & RecomposeProps) =>
    <div>
        <label>
            <input
                type="radio"
                id="edit-report-yearly-use-daynum"
                checked={ useDayNum }
                onChange={ handleChangeUseDayNum }
                disabled={ !enabled }
            />
            { sentenceFragments.map(renderFragment) }
        </label>
    </div>
;

type RecomposeProps = {
    sentenceFragments: string[];
    renderFragment: FragmentRenderer;
    month: string;
    dayNum: string;
    frequency: string;
    handleChangeUseDayNum: React.ChangeEventHandler<HTMLInputElement>;
    handleChangeMonth: React.ChangeEventHandler<HTMLSelectElement>;
    handleChangeDayNum: React.ChangeEventHandler<HTMLInputElement>;
    handleChangeFrequency: React.ChangeEventHandler<HTMLInputElement>;
};
type _RecomposeProps = RecomposeProps & {
    _setUseDayNum: (b: boolean) => void;
    _setMonth: (m: string) => void;
    _setDayNum: (d: string) => void;
    _setFrequency: (f: string) => void;
};

const update = ({ onChange, useDayNum, onChangeUseDayNum, month, dayNum, frequency }: OwnProps & _RecomposeProps) => {
    if (useDayNum) {
        onChange(reportSettings =>
            reportSettings
                .setScheduleYearlyMonth(month)
                .setScheduleYearlyDayNum(parseInt(dayNum, 10))
                .setScheduleFrequency(parseInt(frequency, 10))
        );
    }
};

export const RecurrenceYearlyDayNum = compose<OwnProps & RecomposeProps, OwnProps>(
    setDisplayName('RecurrenceYearly'),
    withState('month', '_setMonth',  ({ reportSettings }: OwnProps) =>
        reportSettings.useScheduleYearlyDayNum
            ? reportSettings.scheduleYearlyMonth
            : 'January'
    ),
    withState('dayNum', '_setDayNum', ({ reportSettings }: OwnProps) => reportSettings.scheduleYearlyDayNum),
    withState('frequency', '_setFrequency', ({ reportSettings }: OwnProps) =>
        reportSettings.scheduleType === 'Yearly'
            ? reportSettings.useScheduleYearlyDayNum
                ? reportSettings.scheduleFrequency.toString()
                : '1'
            : '1',
    ),
    withHandlers({
        handleChangeUseDayNum: (props: OwnProps & _RecomposeProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const useDayNum = e.target.checked;
            props.onChangeUseDayNum(useDayNum);
            update({ ...props, useDayNum });
        },
        handleChangeMonth: (props: OwnProps & _RecomposeProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const useDayNum = true;
            const month = e.target.value;
            props._setMonth(month);
            props.onChangeUseDayNum(useDayNum);
            update({ ...props, month, useDayNum });
        },
        handleChangeDayNum: (props: OwnProps & _RecomposeProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const useDayNum = true;
            const dayNum = e.target.value;
            props._setDayNum(dayNum);
            props.onChangeUseDayNum(useDayNum);
            update({ ...props, dayNum, useDayNum });
        },
        handleChangeFrequency: (props: OwnProps & _RecomposeProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const useDayNum = true;
            const frequency = e.target.value;
            props._setFrequency(frequency);
            props.onChangeUseDayNum(useDayNum);
            update({ ...props, frequency, useDayNum });
        },
    }),
    lifecycle({
        componentWillUpdate(newProps: OwnProps & _RecomposeProps) {
            // If this component becomes enabled, need to apply the current values
            // to make sure the XML reflects the values in the UI.
            if (!this.props.enabled && newProps.enabled) {
                update(newProps);
            }
        },
    }),
    withProps(({ frequency, t, enabled, ...props }: OwnProps & RecomposeProps) => ({
        sentenceFragments: makeSentenceFragments(t('WC_REPORT_SCHEDULE_YEARLY_DAYNUM_SENTENCE', { count: parseInt(frequency, 10) })),
        renderFragment: renderFragment({
            MONTH: (
                <MonthPicker
                    id="edit-report-yearly-daynum-month"
                    value={ props.month }
                    onChange={ props.handleChangeMonth }
                    disabled={ !enabled }
                />
            ),
            DAYNUM: (
                <Input
                    type="number"
                    id="edit-report-yearly-daynum"
                    value={ props.dayNum }
                    onChange={ props.handleChangeDayNum }
                    min={ 1 }
                    max={ 31 }
                    maxLength={ 2 }
                    disabled={ !enabled }
                />
            ),
            INTERVAL: (
                <Input
                    type="number"
                    id="edit-report-yearly-daynum-interval"
                    value={ frequency }
                    onChange={ props.handleChangeFrequency }
                    min={ 1 }
                    max={ 12 }
                    maxLength={ 2 }
                    disabled={ !enabled }
                />
            ),
        }),
    })),
)(RawRecurrenceYearlyDayNum);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/schedule/runOnSchedule/recurrence/RecurrenceYearlyDayNum.tsx